import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import {
  processAndUploadGIF,
  processAndUploadPhotos
} from '@renderer/utils/pipeline/outputOrchestrator'
import type { ProcessingContext } from '@renderer/types/outputPipeline.types'
import {
  OncePerSession,
  bakesComplete,
  photoUrlsFor,
  settleWithin,
  waitUntil
} from './galleryReady'

/** The signed-in operator, handed down by ProjectRoute like on every stock page. */
export interface UserContext {
  user?: {
    email: string
  }
}

/**
 * Where this session's media goes: the same context the stock pages build.
 *
 * The user is what the upload is keyed and authorised by — without it
 * `uploadFile` skips the upload outright (and the strip was logged as
 * "completed" anyway), which is how BK went a week with nothing in any
 * gallery. The gallery id is the session's galleryKey, as stock uses.
 */
export function bkUploadContext(
  context: UserContext | null,
  projectId: string | undefined,
  downloadLocal: boolean
): ProcessingContext {
  const live = useSessionStateStore.getState()
  return {
    context,
    projectId,
    sessionId: live.id || '',
    galleryId: live.galleryKey || live.gallery?.id || '',
    downloadLocal,
    isOnline: navigator.onLine
  }
}

/**
 * How long the upload waits for the last filter bakes before it sends the raw
 * shot for any photograph still without one. A bake lands a moment after its
 * shutter, so this only matters when capture is confirmed right after a shot.
 */
const BAKE_WAIT_MS = 15000

const photoUploads = new OncePerSession()

/**
 * The guest's photographs and (when the project sells one) the GIF, uploaded
 * once per session with the chosen filter applied — the gallery shows each
 * photo the way the strip printed it.
 *
 * Started when the guest confirms capture, because the photographs are final
 * there: retakes happen on Capture, and border, stickers and print never
 * change them. BK used to start at "print now", and on the event's Wi-Fi the
 * gallery then filled 12-22 s later, while the QR was already on screen
 * (sessions blh6kn and vm6jbl, 2026-10-02): a guest who scanned at once got
 * "No images found". Border and stickers take the guest about fifteen seconds,
 * which is the head start the photographs need.
 *
 * Uploaded, not downloaded again: Capture already saved each photograph on the
 * booth as it was taken (stock does the same). Downloading twelve more files
 * here also tripped the browser's multiple-download block, which could hold
 * back the strip download right behind them.
 */
export function startBkPhotoUpload(ctx: ProcessingContext): Promise<void> {
  const { logActivity } = useSessionStateStore.getState()
  // Checked before the upload counts as started: a call that cannot upload
  // must leave the next caller (Select, before printing) free to try.
  if (!ctx.context?.user?.email) {
    logActivity('BK upload skipped: no signed-in user on this booth', { level: 'error' })
    return Promise.resolve()
  }
  if (!ctx.sessionId) {
    logActivity('BK upload skipped: no session id yet', { level: 'error' })
    return Promise.resolve()
  }

  return photoUploads.run(ctx.sessionId, async () => {
    const filterId = useSessionStateStore.getState().userPreferences?.filterId
    const shots = (): Parameters<typeof photoUrlsFor>[0] =>
      useSessionStateStore.getState().gallery?.capturedItems ?? []
    const baked = await waitUntil(() => bakesComplete(shots(), filterId), BAKE_WAIT_MS)
    if (!baked) {
      logActivity('BK upload: filter still baking, sending the raw shot for the rest', {
        level: 'warning'
      })
    }

    const photoUrls = photoUrlsFor(shots(), filterId)
    if (photoUrls.length === 0) return

    if (useSessionStateStore.getState().userPreferences?.useGIF) {
      const capture = useConfigStore.getState().config?.pages?.capture?.settings
      void processAndUploadGIF(photoUrls, capture?.gifSpeed || 1000, ctx, capture?.customGifFrame)
    }

    await processAndUploadPhotos(
      photoUrls.map((photoUrl) => ({ photoUrl })),
      { ...ctx, downloadLocal: false }
    )
    // The guest may have finished by now, and the store already belongs to the
    // next one: the flag goes on this session or nowhere.
    const live = useSessionStateStore.getState()
    if (live.id !== ctx.sessionId) return
    live.setSessionState({
      gallery: {
        ...live.gallery,
        individualPhotosUploaded: true,
        individualPhotosUploadedAt: Date.now()
      }
    })
  })
}

const stripUploads = new Map<string, Promise<unknown>>()

/**
 * Select hands the strip's upload over here: it outlives the page (Select
 * moves on after two seconds so the printer never waits), and Result needs it.
 */
export function trackBkStripUpload(sessionId: string, upload: Promise<unknown>): void {
  stripUploads.set(sessionId, upload)
}

/**
 * The longest the QR waits for the gallery. The strip alone takes a few
 * seconds on the event's Wi-Fi once the photographs went ahead at Capture;
 * twenty seconds leaves room for a slow link, and a dead one never leaves the
 * guest without a QR — the booth's retry queue fills the gallery later, and
 * the QR still leads there.
 */
const QR_WAIT_CAP_MS = 20000

/** Settles once this session's photographs and strip are uploaded, or at the cap. */
export function whenBkGalleryReady(
  sessionId: string
): Promise<{ waitedMs: number; capped: boolean }> {
  const uploads = [photoUploads.get(sessionId), stripUploads.get(sessionId)].filter(
    (upload): upload is Promise<unknown> => Boolean(upload)
  )
  return settleWithin(uploads, QR_WAIT_CAP_MS)
}

/** The session is over; its uploads carry on without anyone waiting on them. */
export function forgetBkUploads(sessionId: string): void {
  photoUploads.forget(sessionId)
  stripUploads.delete(sessionId)
}
